import { useState, useEffect } from "react";
import apiClient from "../../services/api";
import { toast } from "sonner";
import { Plus, Trash2, Award } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../ui/alert-dialog";
const TrainingTab = ({ getAuthHeaders, onRefresh }) => {
  const [certTypes, setCertTypes] = useState([]);
  const [newCertName, setNewCertName] = useState("");
  const [loading, setLoading] = useState(true);

  // Fetch on mount
  useEffect(() => {
    const fetchTypes = async () => {
      try {
        const res = await apiClient.get(`/training/certificate-types`);
        setCertTypes(res.data);
      } catch {
        toast.error("Failed to load certificate types");
      } finally {
        setLoading(false);
      }
    };
    fetchTypes();
  }, [getAuthHeaders]);

  const handleAdd = async () => {
    const name = newCertName.trim();
    if (!name) return;
    try {
      const res = await apiClient.post(
        `/training/certificate-types`,
        { name }
      );
      setCertTypes(res.data);
      setNewCertName("");
      toast.success("Certificate type added");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add certificate type");
    }
  };

  const handleDelete = async (id) => {
    try {
      const res = await apiClient.delete(
        `/training/certificate-types/${id}`
      );
      setCertTypes(res.data);
      toast.success("Certificate type deleted");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    }
  };

  if (loading) {
    return <div className="animate-pulse text-muted-foreground p-8 text-center">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Award className="w-5 h-5" />
            Certificate Types
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Add certificate names that can be tracked for employee training compliance.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Add new */}
          <div className="flex gap-2">
            <Input
              placeholder="e.g., OSHA 10, CPR/First Aid, Scaffold Safety..."
              value={newCertName}
              onChange={(e) => setNewCertName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleAdd()}
              data-testid="new-cert-type-input"
            />
            <Button onClick={handleAdd} disabled={!newCertName.trim()} data-testid="add-cert-type-btn">
              <Plus className="w-4 h-4 mr-1" /> Add
            </Button>
          </div>

          {/* List */}
          {certTypes.length === 0 ? (
            <p className="text-center text-muted-foreground py-6">
              No certificate types added yet. Add one above to get started.
            </p>
          ) : (
            <div className="space-y-2">
              {certTypes
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((ct) => (
                  <div
                    key={ct.id}
                    className="flex items-center justify-between p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors"
                    data-testid={`cert-type-${ct.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <Award className="w-4 h-4 text-primary" />
                      <span className="font-medium">{ct.name}</span>
                    </div>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive/60 hover:text-destructive"
                          data-testid={`delete-cert-type-${ct.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete "{ct.name}"?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This will remove this certificate type from the list. Existing training records using this type will not be affected.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => handleDelete(ct.id)}
                            className="bg-destructive text-destructive-foreground"
                          >
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TrainingTab;
